import { App, JSONSafe, Provision, Task } from "@/types";
import { ComponentRef, useCallback, useRef, useState } from "react";

import logger from "@/lib/logger";
import { cn, fetcherIgnore404, okstatus } from "@/lib/utils";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Checkbox } from "@/components/ui/checkbox";
import useSWR from "swr";
import { getProviderMeta } from "@/lib/provider/meta";
import { Alert, AlertDescription } from "@/components/ui/alert";

export const DeleteHostDialog = ({
  id,
  onDelete,
  children,
  disabled
}: {
  id: string;
  onDelete?: () => void;
  children?: (onDelete: () => void) => React.ReactNode;
  disabled?: boolean;
}) => {
  const [removeFromProvider, setRemoveFromProvider] = useState(false);
  const [deleteHost, setDeleteHost] = useState(false);
  const deleteRef = useRef<ComponentRef<typeof ConfirmDialog>>(null);
  const { data: provision } = useSWR<JSONSafe<Provision>>(
    `/api/host/${id}/provision`,
    fetcherIgnore404
  );
  const provider =
    provision && "provider" in provision ? getProviderMeta(provision?.provider) : undefined;
  const { data: apps } = useSWR<JSONSafe<App[]>>(`/api/app?hostId=${id}`);

  const handleDelete = useCallback(async () => {
    await fetch(`/api/host/${id}`, {
      method: "DELETE"
    }).then(okstatus);

    onDelete && onDelete();
  }, [id, onDelete]);

  const handleRemoveFromHost = useCallback(
    (deleteHost: boolean) => async () => {
      try {
        const response = await fetch(`/api/host/${id}/deprovision`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            deleteHost: !!deleteHost
          })
        })
          .then(okstatus)
          .then(res => res.json() as Promise<JSONSafe<Task>>);
        logger.info("Task created", response);
        onDelete && onDelete();
      } catch (error) {
        logger.warn(error);
      }
    },
    [id, onDelete]
  );

  const handleOpen = useCallback(() => {
    deleteRef.current?.open();
  }, []);

  return (
    <>
      <ConfirmDialog
        ref={deleteRef}
        onContinue={
          removeFromProvider
            ? handleRemoveFromHost(deleteHost)
            : deleteHost && !removeFromProvider
              ? handleDelete
              : undefined
        }
        title="Delete host"
        className="flex flex-col gap-4"
      >
        <div className="text-md font-bold">Are you sure you want to delete this host?</div>

        {apps && apps.length > 0 ? (
          <Alert variant="destructive">
            <AlertDescription>
              <div className="flex flex-row gap-2 items-center">
                <span className="text-lg">⚠️</span>
                <div className="font-semibold mb-1 text-lg">Host in use</div>
              </div>
              <div>
                This host is used by the app{apps.length > 1 ? "s" : ""} &ldquo;
                {apps.map(app => app.name).join(", ")}&rdquo;.
              </div>
              <div className="mt-1">
                Please remove {`app${apps.length > 1 ? "s" : ""}`} or assign another host
                before removing this host.
              </div>
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <label
              className={cn(
                "flex flex-row gap-2 items-start text-left text-accent-foreground",
                !provision && "opacity-50"
              )}
            >
              <Checkbox
                checked={removeFromProvider}
                onCheckedChange={checked => setRemoveFromProvider(!!checked)}
                disabled={
                  !provision ||
                  !provision.providerId ||
                  provision.status !== "completed" ||
                  !provider
                }
              />
              Remove host from provider {provider?.name}{" "}
              {!provider && provision
                ? "(unknown provider " + provision?.provider + ")"
                : !provision || !provision?.providerId
                  ? "(not yet provisioned)"
                  : provision?.status === "removed"
                    ? "(already removed, " + provision?.providerId + ")"
                    : "(" + provision?.status + ", " + provision?.providerId + ")"}
            </label>

            <label className="flex flex-row gap-2 items-center text-accent-foreground">
              <Checkbox
                checked={deleteHost}
                onCheckedChange={checked => setDeleteHost(!!checked)}
                disabled={false}
              />
              Delete local host record
            </label>

            <div className="text-accent-foreground text-sm line-clamp-2 h-12">
              {deleteHost && !removeFromProvider
                ? "We will only delete local host record. The host will continue to run."
                : deleteHost && removeFromProvider
                  ? "We will remove the host from the provider and delete local host record."
                  : !deleteHost && removeFromProvider
                    ? "We will remove the host from the provider. You can provision it on another instance."
                    : "Please select an option to continue."}
            </div>
          </>
        )}
      </ConfirmDialog>

      {children ? (
        // eslint-disable-next-line react-hooks/refs
        children(handleOpen)
      ) : (
        <DropdownMenuItem
          disabled={disabled}
          onClick={e => {
            e.preventDefault();
            deleteRef.current?.open();
          }}
        >
          Delete
        </DropdownMenuItem>
      )}
    </>
  );
};
